import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AchievementToast from './AchievementToast';
import XpToast from './XpToast';
import HoursToast from './HoursToast';
import './Layout.css';

function useScrollReveal(containerRef) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const applyRevealClass = () => {
      const targets = container.querySelectorAll('.card, .stat-grid, .stat-card, h1, h3:not(.scroll-reveal-skip)');
      targets.forEach(el => {
        if (!el.classList.contains('scroll-reveal')) {
          el.classList.add('scroll-reveal');
        }
      });
    };

    // Apply on mount and on DOM changes
    applyRevealClass();
    const mo = new MutationObserver(applyRevealClass);
    mo.observe(container, { childList: true, subtree: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    const observe = () => {
      container.querySelectorAll('.scroll-reveal:not(.revealed)').forEach(el => observer.observe(el));
    };
    observe();
    const interval = setInterval(observe, 300);

    return () => {
      clearInterval(interval);
      observer.disconnect();
      mo.disconnect();
    };
  }, [containerRef]);
}


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const burgerRef = useRef(null);
  const mainRef = useRef(null);

  useScrollReveal(mainRef);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Close on route change
  useEffect(() => { closeSidebar(); }, [location.pathname, closeSidebar]);

  // ESC key closes sidebar
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && sidebarOpen) closeSidebar();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [sidebarOpen, closeSidebar]);

  // Trap focus inside sidebar when open
  useEffect(() => {
    if (!sidebarOpen || !sidebarRef.current) return;
    const sidebar = sidebarRef.current;
    const focusable = sidebar.querySelectorAll('a, button, [tabindex]');
    if (focusable.length > 0) focusable[0].focus();
  }, [sidebarOpen]);

  const userNav = [
    { to: '/', label: 'Dashboard', icon: '\u2302' },
    { to: '/spin', label: 'Spin the Leaf', icon: '\uD83C\uDF43' },
    { to: '/rewards', label: 'Stash Shop', icon: '\uD83C\uDFA8' },
    { to: '/bookings', label: 'Pre-Order Pickup', icon: '\uD83D\uDCE6' },
    { to: '/avatar', label: 'Avatar', icon: '\uD83E\uDDD1' },
    { to: '/profile', label: 'My Stash', icon: '\uD83E\uDD94' },
  ];

  const adminNav = [
    { to: '/admin', label: 'Grow Room', icon: '\u2699' },
  ];

  return (
    <div className="layout">
      <AchievementToast />
      <XpToast />
      <HoursToast />
      {/* Clean Header */}
      <header className="navbar">
        <div className="nav-inner">
          <Link to="/" className="nav-brand">Greenhouse Club</Link>
          <div className="nav-user-info">
            <span className="nav-points">{user?.points || 0} BP</span>
          </div>
          <button
            ref={burgerRef}
            className="burger-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
          >
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </button>
        </div>
      </header>

      {/* Overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          <span className="sidebar-brand">Greenhouse Club</span>
          <button
            className="sidebar-close"
            onClick={closeSidebar}
            aria-label="Close navigation menu"
          >
            &times;
          </button>
        </div>

        {/* User Section */}
        <div className="sidebar-section">
          <div className="sidebar-section-label">Menu</div>
          {userNav.map(item => (
            <Link
              key={item.to}
              to={item.to}
              className={`sidebar-link ${location.pathname === item.to ? 'active' : ''}`}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>

        {/* Admin Section */}
        {user?.is_admin && (
          <div className="sidebar-section">
            <div className="sidebar-divider" />
            <div className="sidebar-section-label">Admin</div>
            {adminNav.map(item => (
              <Link
                key={item.to}
                to={item.to}
                className={`sidebar-link ${location.pathname === item.to ? 'active' : ''}`}
              >
                <span className="sidebar-icon">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-user-info">
            <div className="sidebar-username">{user?.username}</div>
            <div className="sidebar-points">{user?.points || 0} Bud Points</div>
          </div>
          <button className="sidebar-logout" onClick={() => { logout(); closeSidebar(); }}>
            Logout
          </button>
        </div>
      </aside>

      <main className="main-content" ref={mainRef}>
        <div className="page-transition" key={location.pathname}>
          {children}
        </div>
      </main>
    </div>
  );
}
